import { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import LoginForm from '../../Components/User/LoginForm/LoginForm';
import CustomLink from '../../common/CustomLink/CustomLink';
import { Icon } from '../../common/Icon/Icon';
import { useDataContext } from '../../core/context/data.context';
import { useUserContext } from '../../core/context/user.context';
import { addBackground, toBool } from '../../core/helpers';
import useModal from '../../core/hooks/useModal';
import './Login.css';

function Login() {
  const [searchParams] = useSearchParams();
  const useEmailAndPassword = searchParams.get('password');
  const modalOptions = useMemo(
    () => ({
      keepOpen: true,
    }),
    [],
  );
  const { modal } = useModal(modalOptions);
  const { user } = useUserContext();
  const navigate = useNavigate();
  const {
    data: { generalData, landingPageData },
    setPage,
  } = useDataContext();

  useEffect(() => {
    if (user.email) navigate('/');
  }, [navigate, user]);

  useEffect(() => {
    setPage('');
  }, [setPage]);

  if (!generalData.seo) return null; // Not the ideal solution to not show the default login modal before landingPageData is loaded

  return (
    <>
      <Helmet>
        {!generalData?.seo?.title ? null : <title>{generalData?.seo?.title}</title>}
        {!generalData?.seo?.description ? null : (
          <meta name="description" content={generalData?.seo?.description} />
        )}
      </Helmet>
      <div className="login" {...addBackground({ img: generalData.img })} />
      {modal({
        children: (
          <>
            {!useEmailAndPassword ? null : (
              <Link className="modal__back" to="/login">
                <Icon.Arrow className="icon-large flip-x" />
                <span className="sr-only">Back</span>
              </Link>
            )}
            <Link className="modal__close" to="/">
              <Icon.Plus className="icon-large" />
              <span className="sr-only">Home page</span>
            </Link>
            <div className="login__container flex flex--center gap-xs">
              {landingPageData && !useEmailAndPassword ? (
                <>
                  <h1 className="login__title h3">{landingPageData.loginTitle}</h1>
                  {!landingPageData.allowNormalLogin ? null : (
                    <CustomLink
                      className="login__cta"
                      link={{
                        text: 'Log in with email and password',
                        href: '?password=true',
                        color: 'light',
                      }}
                    />
                  )}
                  {!landingPageData.sso_connections?.length
                    ? null
                    : landingPageData.sso_connections.map((connection) => (
                        <CustomLink
                          key={connection._id}
                          className="login__cta"
                          link={{
                            text: connection.loginBtnLbl,
                            href: connection.idpUrl,
                            color: 'light',
                          }}
                        />
                      ))}
                </>
              ) : (
                <>
                  <h1 className="login__title h2">Log in</h1>
                  <LoginForm />
                  {toBool(process.env.REACT_APP_HIDE_REGISTRATION) ? null : (
                    <Link className="link" to="/apply">
                      Create an account
                    </Link>
                  )}
                </>
              )}
            </div>
          </>
        ),
        keepMounted: true,
      })}
    </>
  );
}

export default Login;
